// Shared operational instructor resolver for admin surfaces.
//
// "Who is teaching this class right now?" must have exactly ONE answer across
// the class board, roster, schedule, payroll preview, and class editor. This
// module answers it from `instructor_class_assignments` rows WITHOUT mutating
// or discarding history: historical rows stay in the database for payroll and
// audit, they are simply not part of the operational answer.
//
// Rules (mirrors public.resolve_class_instructor precedence):
//   1. Only currently-live statuses count (active / upcoming / temporary /
//      confirmed). completed, replaced, cancelled, declined, ended, expired,
//      superseded, historical, merged, etc. are history.
//   2. An assignment whose end_date is in the past is expired — excluded.
//   3. An assignment whose start_date is in the future is excluded UNLESS it
//      belongs to the current/upcoming canonical session (or no currently
//      dated assignment exists at all, in which case the soonest upcoming one
//      is the operational answer).
//   4. When the caller knows the current/upcoming session label and any valid
//      row matches it, ONLY those rows are considered — assignments scoped to
//      other sessions are unrelated.
//   5. Two or more surviving DISTINCT instructors = genuinely co-taught.
//   6. Zero surviving instructors = "Instructor TBD" (never a historical name).

export interface OperationalAssignmentRow {
  instructor_id: string | null;
  class_id?: string | null;
  class_slug?: string | null;
  status?: string | null;
  assignment_kind?: string | null;
  session_label?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  temporary_until_replaced?: boolean | null;
}

// Mirrors LIVE_ASSIGNMENT_STATUSES in src/lib/staffing.ts (canonical lifecycle).
const LIVE_STATUSES = new Set(["active", "upcoming", "future", "temporary", "confirmed"]);

function isPendingStatus(status: string): boolean {
  return (
    status.includes("pending") ||
    status.includes("proposed") ||
    status.includes("offered") ||
    status.includes("invited")
  );
}

function toDay(value?: string | Date | null): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}


function todayKey(today: Date): string {
  return today.toISOString().slice(0, 10);
}

function normalizeLabel(label?: string | null): string {
  return (label ?? "").trim().toLowerCase();
}

export interface OperationalInstructorResult {
  /** Distinct instructor ids genuinely responsible for the active session. */
  instructorIds: string[];
  /** True when the only thing on the class is an unaccepted offer/proposal. */
  pending: boolean;
  /** The rows that survived filtering — useful for debugging/audit output. */
  rows: OperationalAssignmentRow[];
}

export function resolveOperationalInstructors(
  rows: OperationalAssignmentRow[] | undefined | null,
  opts: {
    sessionLabel?: string | null;
    /** Canonical current/next session window (YYYY-MM-DD or Date). */
    sessionStart?: string | Date | null;
    sessionEnd?: string | Date | null;
    today?: Date;
  } = {},

): OperationalInstructorResult {
  const all = rows ?? [];
  const today = todayKey(opts.today ?? new Date());
  const wantedSession = normalizeLabel(opts.sessionLabel);

  let pending = false;
  const live: OperationalAssignmentRow[] = [];

  for (const r of all) {
    if (!r.instructor_id) continue;
    const status = (r.status ?? "").toLowerCase().trim();
    if (isPendingStatus(status)) {
      pending = true;
      continue;
    }
    if (!LIVE_STATUSES.has(status)) continue; // history — kept in DB, ignored here

    const end = toDay(r.end_date);
    if (end && end < today) continue; // expired / replaced window

    live.push(r);
  }

  // Session scoping: the CANONICAL current/next session of the class is the
  // anchor. A live row belongs to that session when its label matches it, or
  // when its own date window overlaps the session's window. Rows scoped to a
  // different named session (or to a window that ended before this session
  // starts) are history for this card and never fall through as a default.
  const sessionStart = toDay(opts.sessionStart ?? null);
  const sessionEnd = toDay(opts.sessionEnd ?? null);

  const overlapsSession = (r: OperationalAssignmentRow): boolean => {
    if (!sessionStart && !sessionEnd) return false;
    const rs = toDay(r.start_date);
    const re = toDay(r.end_date);
    if (!rs && !re) return false; // undated row — neutral, not an overlap
    if (sessionEnd && rs && rs > sessionEnd) return false;
    if (sessionStart && re && re < sessionStart) return false;
    return true;
  };

  let scoped = live;
  if (wantedSession || sessionStart || sessionEnd) {
    const belongs = live.filter(
      (r) =>
        (wantedSession && normalizeLabel(r.session_label) === wantedSession) || overlapsSession(r),
    );
    if (belongs.length) {
      scoped = belongs;
    } else {
      // No row is tied to this session. Only fully generic rows (no session
      // label AND no dates) may stand in; a row labelled for a different
      // session must never become this card's instructor.
      scoped = live.filter(
        (r) => !normalizeLabel(r.session_label) && !toDay(r.start_date) && !toDay(r.end_date),
      );
    }
  }


  // Date scoping: prefer assignments already in effect today.
  const current = scoped.filter((r) => {
    const start = toDay(r.start_date);
    return !start || start <= today;
  });

  let effective = current;
  if (!effective.length) {
    // Nothing in effect yet — the soonest upcoming assignment is operational.
    const upcoming = scoped
      .filter((r) => {
        const start = toDay(r.start_date);
        return !!start && start > today;
      })
      .sort((a, b) => (toDay(a.start_date) ?? "").localeCompare(toDay(b.start_date) ?? ""));
    if (upcoming.length) {
      const soonest = toDay(upcoming[0].start_date);
      effective = upcoming.filter((r) => toDay(r.start_date) === soonest);
    }
  }

  const ids: string[] = [];
  for (const r of effective) {
    if (r.instructor_id && !ids.includes(r.instructor_id)) ids.push(r.instructor_id);
  }

  return { instructorIds: ids, pending: pending && ids.length === 0, rows: effective };
}

/**
 * Canonical "who is operationally teaching this session" answer.
 *
 * BUSINESS RULE: `classes.instructor_id` is ADMINISTRATIVE METADATA ONLY. It is
 * a class contact / historical default and must NEVER make a future session
 * look staffed. Operational staffing precedence:
 *   1. a valid session-specific assignment for the current/next session;
 *   2. otherwise a deliberate class-wide assignment row (permanent, live
 *      status, no session label, no dates) — handled inside
 *      resolveOperationalInstructors;
 *   3. otherwise Instructor Needed / TBD.
 *
 * The class-level id is still returned as `classDefaultInstructorId` so admin
 * surfaces can show an advisory hint ("Class default on file: … — confirm and
 * assign") without claiming the session is covered.
 */
export function resolveInstructorsWithClassDefault(
  rows: OperationalAssignmentRow[] | undefined | null,
  opts: Parameters<typeof resolveOperationalInstructors>[1] & {
    classLevelInstructorId?: string | null;
  } = {},
): OperationalInstructorResult & {
  source: "assignment" | "none";
  classDefaultInstructorId: string | null;
} {
  const result = resolveOperationalInstructors(rows, opts);
  const classDefaultInstructorId = opts.classLevelInstructorId ?? null;
  if (result.instructorIds.length) {
    return { ...result, source: "assignment", classDefaultInstructorId };
  }
  return { ...result, source: "none", classDefaultInstructorId };
}


export type OperationalInstructorState =

  | "assigned"
  | "missing"
  | "multiple"
  | "pending"
  | "external";

/** Convenience: turn the resolver result into the card badge state. */
export function operationalInstructorState(
  result: OperationalInstructorResult,
  fallbackInstructorId?: string | null,
): OperationalInstructorState {
  const count = result.instructorIds.length || (fallbackInstructorId ? 1 : 0);
  if (count === 0) return result.pending ? "pending" : "missing";
  if (count > 1) return "multiple";
  return "assigned";
}
